import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TableCell from "@mui/material/TableCell"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"
import { useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import AdminEntityAutocomplete from "../components/AdminEntityAutocomplete"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"

const YES_NO = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
]

export const ASSIGNMENT_FILTER_SPECS = {
  status: { default: "active", param: "status" },
  concierge: { param: "concierge_user_id" },
  account: { param: "owner_user_id" },
  number: { param: "has_number" },
  mailbox: { param: "has_mailbox" },
}

const TOOLBAR_FILTERS = [
  {
    key: "status",
    label: "Status",
    type: "select",
    anyLabel: null,
    options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "revoked", label: "Revoked" },
      { value: "all", label: "All" },
    ],
  },
  { key: "concierge", label: "Concierge", type: "entity", entity: "concierge" },
  {
    key: "account",
    label: "Parent account",
    type: "entity",
    entity: "account",
    placeholder: "Type 2+ letters",
  },
  { key: "number", label: "Has number", type: "select", options: YES_NO },
  { key: "mailbox", label: "Has mailbox", type: "select", options: YES_NO },
]

function formatPerson(name, email) {
  if (!email) return name || "—"
  return name ? `${name} (${email})` : email
}

export default function AdminAssignments() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [concierge, setConcierge] = useState(null)
  const [owners, setOwners] = useState([])
  const [formError, setFormError] = useState(null)
  const [saving, setSaving] = useState(false)

  const query = useAdminPaginatedQuery(adminApi.fetchAssignments, {
    filters: ASSIGNMENT_FILTER_SPECS,
    defaultSortColumn: "created",
    defaultSortDirection: "desc",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  function closeDialog() {
    setDialogOpen(false)
    setConcierge(null)
    setOwners([])
    setFormError(null)
  }

  async function handleCreate() {
    setFormError(null)
    setSaving(true)
    try {
      const { data } = await adminApi.createAssignment({
        concierge_user_id: concierge.id,
        owner_user_ids: owners.map((owner) => owner.id),
      })
      if (data?.errors?.length) {
        // 207: some owners assigned, some not. Keep the failed ones selected.
        const failedIds = new Set(data.errors.map((e) => e.owner_user_id))
        setOwners((current) => current.filter((o) => failedIds.has(o.id)))
        setFormError(
          data.errors
            .map((e) => e.error || "Could not create assignment.")
            .join(" ")
        )
        refresh({ resetPage: true })
        return
      }
      closeDialog()
      refresh({ resetPage: true })
    } catch (error) {
      const detail = error?.response?.data
      setFormError(
        detail?.errors?.map((e) => e.error).join(" ") ||
          detail?.owner_user_ids ||
          detail?.concierge_user_id ||
          "Could not create assignment."
      )
    } finally {
      setSaving(false)
    }
  }

  async function setStatus(row, status) {
    await adminApi.patchAssignment(row.id, { status })
    refresh()
  }

  async function revoke(row) {
    await adminApi.revokeAssignment(row.id)
    refresh()
  }

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Concierge assignments"
        subtitle="Manage parent-account connections (AccountMembership)."
        actions={
          <Button variant="contained" onClick={() => setDialogOpen(true)}>
            New assignment
          </Button>
        }
      />
      <AdminListToolbar
        query={query}
        noun={totalResultsSize === 1 ? "assignment" : "assignments"}
        filters={TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={8}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="concierge"
              label="Concierge"
              query={query}
              minWidth={{ xs: 88, sm: 120 }}
            />
            <AdminSortableHeaderCell
              columnName="owner"
              label="Parent account"
              query={query}
              minWidth={{ xs: 96, sm: 128 }}
            />
            <AdminSortableHeaderCell
              columnName="status"
              label="Status"
              query={query}
              minWidth={{ xs: 56, sm: 72 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 72, sm: 96 }}>
              Twilio number
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 88, sm: 120 }}>
              Email
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 88, sm: 112 }}>
              Assigned by
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="created"
              label="Created"
              query={query}
              minWidth={{ xs: 72, sm: 88 }}
            />
            <AdminHeaderTableCell align="right" minWidth={{ xs: 64, sm: 80 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((row) => (
          <AdminTableRow key={row.id} hover>
            <AdminTruncatedCell
              value={formatPerson(row.concierge_name, row.concierge_email)}
            />
            <AdminTruncatedCell
              value={formatPerson(row.owner_name, row.owner_email)}
            />
            <AdminTruncatedCell value={row.status} />
            <AdminTruncatedCell value={row.twilio_voice_phone} />
            <AdminTruncatedCell value={row.zoho_mailbox_email} />
            <AdminTruncatedCell
              value={row.assigned_by_name || row.assigned_by_email}
            />
            <AdminTruncatedCell
              value={
                row.created ? format(new Date(row.created), "MMM d, yyyy") : "—"
              }
            />
            <TableCell align="right" sx={adminTableBodyCellSx}>
              <Row gap={1} justifyContent="flex-end">
                {row.status !== "active" && row.status !== "revoked" && (
                  <Button size="small" onClick={() => setStatus(row, "active")}>
                    Activate
                  </Button>
                )}
                {row.status === "active" && (
                  <Button
                    size="small"
                    onClick={() => setStatus(row, "inactive")}
                  >
                    Deactivate
                  </Button>
                )}
                {row.status !== "revoked" && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => revoke(row)}
                  >
                    Revoke
                  </Button>
                )}
              </Row>
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create concierge assignment</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ mt: 1 }}>
            <AdminEntityAutocomplete
              entity="concierge"
              size="medium"
              label="Concierge user"
              placeholder="Search active concierges"
              value={concierge}
              onChange={setConcierge}
            />
            <AdminEntityAutocomplete
              entity="account"
              size="medium"
              multiple
              label="Parent accounts"
              helperText="Search by name or email (min. 2 characters). Pick one or more."
              value={owners}
              onChange={(next) => setOwners(next || [])}
            />
            {formError && (
              <Typography color="error" variant="body2">
                {String(formError)}
              </Typography>
            )}
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!concierge || !owners.length || saving}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Column>
  )
}
